import React, { useState } from "react";
import { cn } from "../../lib/utils";
import { ModelCost } from "../../model_gen/billing";
import { formatCurrency } from "../../utils/FormatUtils";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";

interface ModelPricingTableProps {
  modelPricing: ModelCost[];
  loading: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSize?: number;
  showPagination?: boolean;
}

const ModelPricingTable: React.FC<ModelPricingTableProps> = ({
  modelPricing,
  loading,
  emptyTitle = "No pricing data",
  emptyDescription = "Model pricing information is not available.",
  pageSize = 10,
  showPagination = true
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig<ModelCost>>({ 
    key: null, 
    direction: "desc" 
  });

  const getProviderColor = (provider: string) => {
    const colors: Record<string, string> = {
      "OpenAI": "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      "Anthropic": "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
      "Groq": "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
      "Cerebras": "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      "Sambanova": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      "Gemini": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400",
      "Deepseek": "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400",
      "Maitai": "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    };
    return colors[provider] || "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  };

  const handleSort = (key: keyof ModelCost) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const columns: PaginatedTableColumn<ModelCost>[] = [
    {
      key: "provider",
      header: "Provider",
      width: "w-[20%]",
      sortable: true,
      render: (model: ModelCost) => (
        <span className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getProviderColor(model.provider)
        )}>
          {model.provider}
        </span>
      )
    },
    {
      key: "model",
      header: "Model Name",
      width: "w-[35%]",
      sortable: true,
      render: (model: ModelCost) => (
        <div className="text-sm font-gc-reg text-foreground">
          {model.model}
        </div>
      )
    },
    {
      key: "prompt_cpm",
      header: "Input Cost",
      width: "w-[22.5%]",
      sortable: true,
      render: (model: ModelCost) => (
        <div className="text-sm font-gc-reg text-foreground">
          {formatCurrency(model.prompt_cpm)} / 1M tokens
        </div>
      )
    },
    {
      key: "completion_cpm",
      header: "Output Cost",
      width: "w-[22.5%]",
      sortable: true,
      render: (model: ModelCost) => (
        <div className="text-sm font-gc-reg text-foreground">
          {formatCurrency(model.completion_cpm)} / 1M tokens
        </div>
      )
    }
  ];

  return (
    <PaginatedTable
      data={modelPricing}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={handleSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      showPagination={showPagination}
      loadingRows={8}
    />
  );
};

export default ModelPricingTable; 